import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCheck } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { cn } from '@/lib/cn'
import { formatRelativeTime } from '@/lib/format'
import { IconButton } from '@/components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { toast } from '@/components/ui/toast'
import { useNotificationHub } from '@/features/realtime/useProjectHub'
import { notificationKeys, notificationsApi } from '../api/notificationsApi'
import { notificationHref } from '../notificationLinks'
import type { Notification } from '@/types/notifications'

/**
 * Klokken i topbaren.
 *
 * Tælleren hentes for sig og ikke som en del af listen: den vises altid, og
 * listen kun når panelet er åbent. Ville de dele samme kald, ville hver
 * baggrundsopdatering af tallet hente tyve beskeder, ingen kigger på.
 *
 * Realtidsbeskeden bærer intet indhold — den siger kun "hent igen". Vi
 * ugyldiggør derfor cachen frem for at flette noget ind, og listen kommer
 * altid fra serveren med brugerens egen adgang.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const unread = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: notificationsApi.unreadCount,
    // Realtid er en bekvemmelighed, ikke en garanti. Et roligt interval
    // fanger det, en tabt forbindelse gik glip af.
    refetchInterval: 2 * 60 * 1000,
  })

  const list = useQuery({
    queryKey: notificationKeys.list(false),
    queryFn: () => notificationsApi.list(false),
    enabled: open,
  })

  useNotificationHub(() => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
  })

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    onError: (error) => toast.fromError(error, 'Kunne ikke markere beskederne som læst.'),
  })

  const markOneRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead([id]),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })

  const count = unread.data?.unread ?? 0
  const items = list.data?.items ?? []

  function handleOpen(notification: Notification) {
    setOpen(false)

    if (!notification.isRead) {
      markOneRead.mutate(notification.id)
    }

    const href = notificationHref(notification)

    if (href) {
      void navigate(href)
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <div className="relative">
          <IconButton
            label={count > 0 ? `Notifikationer (${count} ulæste)` : 'Notifikationer'}
            icon={<Bell />}
          />
          {count > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute -right-0.5 -top-0.5 min-w-4 rounded-full px-1',
                'bg-brand-500 text-center text-[10px] font-semibold leading-4 text-white',
              )}
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center justify-between border-b border-line-subtle px-3 py-2.5">
          <p className="text-[13px] font-medium text-text-primary">Notifikationer</p>

          {count > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className={cn(
                'flex items-center gap-1.5 rounded px-1.5 py-1 text-[11px] text-text-muted',
                'transition-colors hover:text-text-primary disabled:opacity-50',
                'focus-visible:outline-2 focus-visible:outline-brand-400',
              )}
            >
              <CheckCheck className="size-3.5" aria-hidden="true" />
              Markér alt som læst
            </button>
          )}
        </div>

        {list.isLoading ? (
          <p className="px-3 py-8 text-center text-[13px] text-text-muted">Henter…</p>
        ) : list.isError ? (
          // En fejl må ikke ligne en tom indbakke — men det er en dropdown,
          // så en diskret linje er nok. Panelet henter igen, når det åbnes.
          <p role="alert" className="px-3 py-8 text-center text-[13px] text-text-muted">
            Notifikationerne kunne ikke hentes. Luk panelet, og prøv igen.
          </p>
        ) : items.length === 0 ? (
          <p className="px-3 py-8 text-center text-[13px] text-text-muted">
            Ingen notifikationer endnu.
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto py-1">
            {items.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => handleOpen(notification)}
                  className={cn(
                    'flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors',
                    'hover:bg-overlay-strong focus-visible:bg-overlay-strong',
                    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-400',
                  )}
                >
                  <span className="flex items-start gap-2">
                    {!notification.isRead && (
                      <span
                        aria-label="Ulæst"
                        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-400"
                      />
                    )}
                    <span
                      className={cn(
                        'min-w-0 flex-1 text-[13px]',
                        notification.isRead ? 'text-text-secondary' : 'text-text-primary',
                      )}
                    >
                      {notification.title}
                    </span>
                  </span>

                  {notification.body && (
                    <span className="line-clamp-2 pl-3.5 text-[12px] text-text-muted">
                      {notification.body}
                    </span>
                  )}

                  <span className="pl-3.5 text-[11px] text-text-muted">
                    {notification.projectName ? `${notification.projectName} · ` : ''}
                    {formatRelativeTime(notification.createdAtUtc)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
